import { type Address, getChainAddresses, type MarketId } from '@morpho-org/blue-sdk'
import { blueAbi } from '@morpho-org/blue-sdk-viem'
import { ZERO } from '@morpho-org/dapp-core'
import { useReadContract } from 'wagmi'

import { useWeb3Context } from '@/contexts/Web3Context'

/**
 * Fast Withdraw-button gate — the lend sister of `useMarketHasPosition`. A single
 * `Morpho.position(marketId, user)` read keyed only on the `marketId` (known on mount), so
 * the Withdraw entry point can appear as soon as the raw position confirms the user holds
 * supply shares, without waiting for the heavier `getPositionData` multicall the operation
 * components run.
 *
 * @param marketId - the on-chain `bytes32` market id (available immediately as a panel prop).
 * @param chainId - the market's chain; defaults to the connected target chain. Pass it
 *   explicitly on multi-chain surfaces (e.g. portfolio Position Manager rows) where the
 *   row's chain can differ from the connected one.
 * @param enabled - whether the caller can expose a Withdraw action and needs the read. `false`
 *   means the caller doesn't need an answer at all, so it resolves to a definite `false` rather
 *   than leaving the caller waiting on a read it never asked for.
 * @returns `true`/`false` once confirmed (or `enabled` is false); `undefined` while the wallet is
 *   still connecting or its read is still pending or has errored — callers must not treat that
 *   as a confirmed absence.
 */
export function useMarketHasSupplyPosition(
  marketId: MarketId | undefined,
  chainId?: number,
  enabled = true
): boolean | undefined {
  const { account, targetChainId } = useWeb3Context()
  const readChainId = chainId ?? targetChainId

  let morpho: Address | undefined
  try {
    morpho = getChainAddresses(readChainId).blue
  } catch {
    morpho = undefined
  }

  const { data } = useReadContract({
    address: morpho,
    abi: blueAbi,
    functionName: 'position',
    args: marketId && account.address ? [marketId, account.address] : undefined,
    chainId: readChainId,
    query: {
      enabled: enabled && !!morpho && !!marketId && !!account.address,
      refetchInterval: 10_000
    }
  })

  if (!enabled) return false
  // Same as `useMarketHasPosition`: a connecting wallet has no address yet, and a confirmed
  // `false` there opens the Lend tab only for the settled read to close it again.
  if (!account.address) return account.isConnecting || account.isReconnecting ? undefined : false
  // Keyed on `data`, not `isSuccess`: a failed poll after a good read flips the status to
  // `error` while the cached tuple stands, and dropping it would hide a live Withdraw entry.
  if (!data) return undefined

  // position() → [supplyShares, borrowShares, collateral]; a lend position exists when the
  // connected account holds supply shares.
  return data[0] > ZERO
}
